
import React from 'react';
import AnimatedCard from '@/components/ui/AnimatedCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTimeRange } from '@/hooks/useTimeRange';
import { useHiddenSeries } from '@/hooks/useHiddenSeries';
import TimeRangeSelector from '@/components/usage/TimeRangeSelector';
import { combinedData } from '@/components/usage/consumptionData';
import ConsumptionAreaChart from '@/components/usage/ConsumptionAreaChart';
import UsageComparisonChart from '@/components/usage/UsageComparisonChart';
import HourlyConsumptionChart from '@/components/usage/HourlyConsumptionChart';
import PeakUsageCard from '@/components/usage/PeakUsageCard';

const UsageGrid = () => {
  const { timeRange, setTimeRange, getFilteredData } = useTimeRange('year');
  const { hiddenSeries, toggleSeriesVisibility } = useHiddenSeries();
  
  // Filter data based on selected time range
  const filteredData = getFilteredData(
    combinedData, 
    timeRange === 'month' ? 1 : timeRange === 'quarter' ? 3 : 12
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8 h-full overflow-auto p-4">
      {/* Combined Consumption Trends */}
      <AnimatedCard
        animation="fade-up"
        delay={150}
        className="border-none shadow-md rounded-xl bg-gradient-to-r from-white/90 to-white/70 dark:from-gray-900/90 dark:to-gray-900/70 backdrop-blur-sm overflow-hidden md:col-span-3 lg:col-span-2"
      >
        <div className="p-4 h-full flex flex-col">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Combined Consumption Trends</h3>
            <TimeRangeSelector 
              timeRange={timeRange}
              onTimeRangeChange={setTimeRange}
            />
          </div>
          <div className="flex-1 min-h-0 overflow-hidden">
            <div className="h-80 w-full">
              <ConsumptionAreaChart 
                data={filteredData}
                hiddenSeries={hiddenSeries}
                toggleSeriesVisibility={toggleSeriesVisibility}
              />
            </div>
          </div>
        </div>
      </AnimatedCard>

      {/* Usage Comparison */}
      <AnimatedCard
        animation="fade-up"
        delay={250}
        className="border-none shadow-md rounded-xl bg-gradient-to-r from-white/90 to-white/70 dark:from-gray-900/90 dark:to-gray-900/70 backdrop-blur-sm overflow-hidden md:col-span-3 lg:col-span-2"
      >
        <div className="p-4 h-full flex flex-col">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Usage Comparison</h3>
          </div>
          <div className="flex-1 min-h-0 overflow-hidden">
            <div className="h-80 w-full">
              <UsageComparisonChart timeRange={timeRange} />
            </div>
          </div>
        </div>
      </AnimatedCard>

      {/* Hourly Consumption Pattern */}
      <AnimatedCard
        animation="fade-up"
        delay={350}
        className="border-none shadow-md rounded-xl bg-gradient-to-r from-white/90 to-white/70 dark:from-gray-900/90 dark:to-gray-900/70 backdrop-blur-sm overflow-hidden md:col-span-2 lg:col-span-2"
      >
        <div className="p-4 h-full flex flex-col">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Daily Consumption Pattern</h3>
          </div>
          <div className="flex-1 min-h-0 overflow-hidden">
            <div className="h-80 w-full">
              <HourlyConsumptionChart />
            </div>
          </div>
        </div>
      </AnimatedCard>

      {/* Peak Usage Stats */}
      <AnimatedCard
        animation="fade-up"
        delay={450}
        className="border-none shadow-md rounded-xl bg-gradient-to-r from-white/90 to-white/70 dark:from-gray-900/90 dark:to-gray-900/70 backdrop-blur-sm overflow-hidden md:col-span-1 lg:col-span-2"
      >
        <div className="p-4 h-full">
          <h3 className="text-lg font-semibold mb-4">Peak Usage</h3>
          <PeakUsageCard />
        </div>
      </AnimatedCard>
    </div>
  );
};

export default UsageGrid;
